<template>
    <div>
        <template v-if="loading">
            <i class="fas fa-cog fa-spin"></i> Loading...
        </template>
        <template v-else>
            <vue-headful :title="'[Agency] ' + agency.agency_name + ' - AMOS365'" />

            <nav aria-label="breadcrumb">
                <ol class="breadcrumb">
                    <li class="breadcrumb-item">
                        <router-link to="/agencies">Agencies</router-link>
                    </li>
                    <li class="breadcrumb-item active" aria-current="page">{{ agency.agency_name }}</li>
                </ol>
            </nav>

            <h4 class="page-header mb-4">
                {{ agency.agency_name }}
            </h4>

            <div class="mb-3" v-if="canEdit">
                <router-link :to="`/agencies/edit/${agency.id}`" class="btn btn-sm btn-default"><i class="fas fa-edit"></i> Edit</router-link>
                <router-link :to="`/contracts/create?seller_type=Agency&seller_id=${agency.id}`" class="btn btn-sm btn-default" title="Assign Product(s)">
                    <i class="fa fa-plus-circle"></i> Assign Product
                </router-link>
            </div>

            <div class="row mb-4">
                <div class="col-md-8 col-12">

                    <div class="card mb-4">
                        <div class="card-header"><strong>Details</strong></div>
                        <div class="card-body">
                            <div class="row">
                                <div class="col-md-6 mb-md-0 mb-4">
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Main Office:</div>
                                        <div class="col-md-7">{{ agency.is_main_office == 1 ? 'Yes' : 'No' }}</div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Website:</div>
                                        <div class="col-md-7">
                                            <a :href="$safeUrl(agency.website)" target="_blank" v-if="agency.website">{{ agency.website }}</a>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Email:</div>
                                        <div class="col-md-7">{{ agency.email }}</div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Phone:</div>
                                        <div class="col-md-7">{{ agency.business_phone }}</div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Last Updated At:</div>
                                        <div class="col-md-7">{{ agency.updated_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Last Updated By:</div>
                                        <div class="col-md-7">{{ agency.last_updated_by }}</div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Marketer:</div>
                                        <div class="col-md-7">
                                            <router-link :to="`/agents/view/${agency.marketer_id}`" v-if="agency.marketer_id">
                                                {{ agency.marketer_name }}
                                            </router-link>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Date Marketer Assigned:</div>
                                        <div class="col-md-7">
                                            <span v-if="agency.marketer_date_assigned">{{ agency.marketer_date_assigned | moment("MM/DD/YYYY") }}</span>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Marketer Relationship Code:</div>
                                        <div class="col-md-7">
                                            <span v-if="agency.marketer_code_id">
                                                {{ agency.marketer_code_label }} <i class="fa fa-question-circle" :id="`marketer-rel-code-${agency.marketer_code_id}`"></i>
                                                <b-tooltip :target="`marketer-rel-code-${agency.marketer_code_id}`" triggers="hover" placement="right">
                                                    <span v-html="$sanitize(agency.marketer_code_desc)"></span>
                                                </b-tooltip>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Master Level:</div>
                                        <div class="col-md-7">
                                            {{ agency.master_level }}
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Active:</div>
                                        <div class="col-md-7">
                                            <label class="badge badge-success" v-if="agency.is_active == 1">Active</label>
                                            <label class="badge badge-warning" v-else-if="agency.is_active == 0">Inactive</label>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Can Contact:</div>
                                        <div class="col-md-7">
                                            <label class="badge badge-danger" v-if="agency.is_do_not_contact == 1">DO NOT CONTACT</label>
                                            <label class="badge badge-default" v-else-if="agency.is_do_not_contact == 0">Okay to Contact</label>

                                            <div v-if="agency.is_do_not_contact == 1 && agency.dnc_code_id">
                                                {{ agency.dnc_code_reason }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Can Contact Downline:</div>
                                        <div class="col-md-7">
                                            <label class="badge badge-danger" v-if="agency.is_do_not_contact_downline == 1">DO NOT CONTACT</label>
                                            <label class="badge badge-default" v-else-if="agency.is_do_not_contact_downline == 0">Okay to Contact</label>

                                            <div v-if="agency.is_do_not_contact_downline == 1 && agency.dnc_downline_code_id">
                                                {{ agency.dnc_downline_code_reason }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Products Managed:</div>
                                        <div class="col-md-7">
                                            {{ agency.num_products_managed }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card mb-4">
                        <div class="card-header"><strong>Principal Agent</strong></div>
                        <div class="card-body">
                            <v-client-table :columns="['last_name', 'npn']" :options="{filterable: false, headings: {last_name: 'Name', npn: 'NPN'}}" :data="agency.agents">

                                <template slot="last_name" slot-scope="props">
                                    <router-link :to="`/agents/view/${props.row.id}`" title="View Agent">{{ props.row.last_name }}, {{ props.row.first_name }}</router-link>
                                </template>

                            </v-client-table>
                        </div>
                    </div>

                    <div class="card mb-4">
                        <div class="card-header"><strong>Identification</strong></div>
                        <div class="card-body">
                            <div class="row mb-3">
                                <div class="col-md-3 text-md-right text-muted">Tax ID:</div>
                                <div class="col-md-7">{{ agency.tax_id | taxId }}</div>
                            </div>
                            <div class="row mb-3">
                                <div class="col-md-3 text-md-right text-muted">NPN:</div>
                                <div class="col-md-7">{{ agency.npn }}</div>
                            </div>
                            <div class="row mb-3">
                                <div class="col-md-3 text-md-right text-muted">Res. License#:</div>
                                <div class="col-md-7">{{ agency.res_license_nbr }}</div>
                            </div>
                            <div class="row mb-3">
                                <div class="col-md-3 text-md-right text-muted">Res. State:</div>
                                <div class="col-md-7">{{ agency.res_state_name }}</div>
                            </div>
                        </div>
                    </div>

                    <div class="card mb-4">
                        <div class="card-header">
                            <strong>Management</strong>
                        </div>
                        <div class="card-body">
                            <div class="row mb-3">
                                <div class="col-md-3 text-md-right text-muted">
                                    Direct Upline Manager:
                                </div>
                                <div class="col-md-7">
                                    <router-link :to="`/agents/view/${agency.direct_manager_id}`" v-if="agency.direct_manager && agency.direct_manager_type === 'Agent'">
                                        {{ agency.direct_manager.first_name }} {{ agency.direct_manager.last_name }}
                                    </router-link>
                                    <router-link :to="`/agencies/view/${agency.direct_manager_id}`" v-else-if="agency.direct_manager && agency.direct_manager_type === 'Agency'">
                                        {{ agency.direct_manager.agency_name }}
                                    </router-link>
                                    <span v-if="agency.direct_manager_type">({{ agency.direct_manager_type }})</span>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-md-3 text-md-right text-muted">
                                    Affiliation Manager:
                                </div>
                                <div class="col-md-7">
                                    <router-link :to="`/agents/view/${agency.direct_manager_id}`" v-if="agency.top_manager && agency.top_manager_type === 'Agent'">
                                        {{ agency.top_manager.first_name }} {{ agency.top_manager.last_name }}
                                    </router-link>
                                    <router-link :to="`/agencies/view/${agency.direct_manager_id}`" v-else-if="agency.top_manager && agency.top_manager_type === 'Agency'">
                                        {{ agency.top_manager.agency_name }}
                                    </router-link>
                                    <span v-if="agency.top_manager_type">({{ agency.top_manager_type }})</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card mb-4">
                        <div class="card-header"><strong>Notes</strong></div>
                        <div class="card-body">
                            <div v-if="agency.notes">{{ agency.notes }}</div>
                            <div v-else>--</div>
                        </div>
                    </div>

                    <div class="card">
                        <div class="card-header">
                            <strong>Contacts:</strong>

                            <div class="float-right" v-if="canEdit">
                                <router-link :to="`/contacts/create?record_type=Agency&record_id=${agency.id}`" class="btn btn-sm btn-default"><i class="fa fa-plus-circle"></i> Add</router-link>
                            </div>
                        </div>
                        <div class="card-body">
                            <contact-list :contacts="contacts" permission="agencies.manage" />
                        </div>
                    </div>
                </div>
                <div class="col-md-4 col-12 mt-md-0 mt-4">
                    <div class="card mb-4">
                        <div class="card-header"><strong>Emails ({{ agency.emails.length }})</strong></div>
                        <div class="card-body">
                            <email-list :emails="agency.emails" />
                        </div>
                    </div>
                    <div class="card mb-4">
                        <div class="card-header"><strong>Phones ({{ agency.phones.length }})</strong></div>
                        <div class="card-body">
                            <phone-list :phones="agency.phones" />
                        </div>
                    </div>
                    <div class="card mb-4">
                        <div class="card-header"><strong>Addresses ({{ agency.addresses.length }})</strong></div>
                        <div class="card-body">
                            <address-list :addresses="agency.addresses" />
                        </div>
                    </div>
                    <div class="card mb-4">
                        <div class="card-header"><strong>Files ({{ files.length }})</strong></div>
                        <div class="card-body">
                            <files-list :files="files" />
                        </div>
                    </div>
                </div>
            </div>

            <ul class="search-nav nav nav-tabs tabs-alt container-m--x mt-4">
                <b-nav-item :active="curTab === 'assigned'" @click="curTab = 'assigned'">
                    Products Assigned ({{ products_assigned.length }})
                </b-nav-item>
                <b-nav-item :active="curTab === 'managed'" @click="curTab = 'managed'">
                    Products Managed ({{ products_managed.length }})
                </b-nav-item>
                <b-nav-item :active="curTab === 'comments'" @click="curTab = 'comments'">
                    <i class="fas fa-comments"></i> Comments ({{ commentCount }})
                </b-nav-item>
                <b-nav-item :active="curTab === 'history'" @click="curTab = 'history'">
                    <i class="fas fa-clock"></i> History ({{ historyCount }})
                </b-nav-item>
            </ul>

            <div class="pt-2">
                <div class="card" v-show="curTab === 'assigned'">
                    <div class="card-body">
                        <products-assigned assetType="Agency" :sellerId="agency.id" sellerType="Agency" :products="products_assigned" :emails="agency.emails" />
                    </div>
                </div>

                <div class="card" v-show="curTab === 'managed'">
                    <div class="card-body">
                        <products-managed assetType="Agency" :managerId="agency.id" managerType="Agency" :products="products_managed" />
                    </div>
                </div>

                <div v-show="curTab === 'comments'">
                    <template v-if="$root.hasPermission('comments.create')">
                        <comments-form ref="agencyCommentForm" assetType="Agency" :assetId="agency.id" @commentCreated="newComment" @commentUpdated="updateComment"></comments-form>
                        <hr/>
                    </template>

                    <comments-list ref="agencyComments" assetType="Agency" :assetId="agency.id" :ajaxLoad="true" @editComment="editComment" @commentCount="((count) => { this.commentCount = count; })"></comments-list>
                </div>
                <div v-show="curTab === 'history'">
                    <div class="card">
                        <div class="card-body">
                            <history-list record_type="Agency" :record_id="agency.id" @historyCount="(count) => { this.historyCount = count; }"></history-list>
                        </div>
                    </div>
                </div>
            </div>
        </template>

    </div>
</template>

<script>
    export default {
        data() {
            return {
                loading: true,
                curTab: 'assigned',
                id: this.$route.params.id,
                userTimezone: this.$root.timezone,
                agency: {},
                parts: [],
                comments: [],
                all_comments: [],
                products_assigned: [],
                products_managed: [],
                files: [],
                contacts: [],
                assignedCount: 0,
                managedCount: 0,
                commentCount: 0,
                historyCount: 0,
            }
        },

        computed: {
            canEdit(){
                //Check Permissions
                return this.$root.hasPermission('agencies.manage');
            },
        },

        methods: {
            //Get the Agency
            getAgency(){
                //Send it
                this.$http.get('/agencies/view/'+this.id)
                    .then(response => {
                        //Set the agency
                        this.agency = response.data.agency;
                        this.products_assigned = response.data.products_assigned;
                        this.products_managed = response.data.products_managed;
                        this.files = response.data.files;
                        this.contacts = response.data.contacts;
                    })
                    .catch(error => {

                    })
                    .then(() => {
                        //Reset
                        this.loading = false;
                    });
            },

            setCount(data){
                //Setter
                this.assignedCount = data;
            },

            editComment(comment){
                //Push into the component
                this.$refs.agencyCommentForm.editComment(comment);
            },

            newComment(comment){
                //Push it
                this.$refs.agencyComments.addComment(comment);
            },

            updateComment(comment){
                //Update it
                this.$refs.agencyComments.updateComment(comment);
            },
        },

        mounted() {
            //Get the agency
            this.getAgency();
        }
    }
</script>
